import authClient from './authClient';

// Types for API requests and responses
export interface RequestCodeRequest {
  email: string;
}

export interface RequestCodeResponse {
  success: boolean;
  message: string;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
}

export interface VerifyCodeResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserInfo {
  id: string;
  email: string;
  created_at: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  features: Record<string, any>;
}

export interface ServiceStatusResponse {
  status: string;
  endpoints: Record<string, any>;
  stats: Record<string, any>;
}

// Authentication API functions
export const authApi = {
  // Health & Status endpoints
  async healthCheck(): Promise<HealthResponse> {
    const response = await authClient.get('/health');
    return response.data;
  },

  async getServiceStatus(): Promise<ServiceStatusResponse> {
    const response = await authClient.get('/auth/status');
    return response.data;
  },

  // Authentication flow
  async requestVerificationCode(data: RequestCodeRequest): Promise<RequestCodeResponse> {
    const response = await authClient.post('/auth/request-code', data);
    return response.data;
  },

  async verifyCodeAndLogin(data: VerifyCodeRequest): Promise<VerifyCodeResponse> {
    const response = await authClient.post('/auth/verify-code', data);
    return response.data;
  },

  async refreshToken(): Promise<RefreshTokenResponse> {
    const response = await authClient.post('/auth/refresh');
    return response.data;
  },

  async getUserInfo(): Promise<UserInfo> {
    const response = await authClient.get('/auth/me');
    return response.data;
  },

  async logout(): Promise<LogoutResponse> {
    const response = await authClient.post('/auth/logout');
    return response.data;
  },
};

// Error handling helper
export const handleAuthError = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  
  if (error.response?.status === 422) {
    return 'Invalid input. Please check your email format.';
  }
  
  if (error.response?.status === 400) {
    return error.response.data?.detail || 'Invalid request. Please try again.';
  }
  
  if (error.response?.status === 401) {
    return 'Authentication failed. Please try again.';
  }
  
  if (error.response?.status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  
  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return 'Network error. Please check if the authentication service is running.';
  }
  
  return 'An unexpected error occurred. Please try again.';
};

export default authApi;
